import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  getDocs,
} from 'firebase/firestore'
import { db } from '../firebase'

export interface Notification {
  id: string
  type: 'lesson' | 'task' | 'message' | 'system'
  title: string
  body: string
  link?: string
  studentId?: string
  studentName?: string
  read: boolean
  createdAt: Timestamp | Date
  metadata?: {
    lessonId?: string
    taskId?: string
    conversationId?: string
  }
  // Phase 2/3: grouped message-notification fields. Present only on
  // type === 'message' docs written by messagingService.upsertMessageNotification.
  conversationId?: string
  otherPartyId?: string
  otherPartyName?: string
  preview?: string
  count?: number
  updatedAt?: Timestamp | Date
}

/**
 * Create a notification for a teacher
 */
export const createNotification = async (
  teacherId: string,
  notification: Omit<Notification, 'id' | 'read' | 'createdAt'>,
): Promise<string> => {
  if (!teacherId) {
    throw new Error('Teacher ID is required')
  }

  try {
    const notificationsRef = collection(
      db,
      'teachers',
      teacherId,
      'notifications',
    )

    const notificationData = {
      ...notification,
      read: false,
      createdAt: serverTimestamp(),
    }

    const docRef = await addDoc(notificationsRef, notificationData)
    console.log('[NOTIFICATION_SERVICE] Created notification:', docRef.id)
    return docRef.id
  } catch (error) {
    console.error('[NOTIFICATION_SERVICE] Error creating notification:', error)
    throw error
  }
}

/**
 * Subscribe to notifications for a teacher (real-time)
 */
export const subscribeToNotifications = (
  teacherId: string,
  callback: (notifications: Notification[]) => void,
): (() => void) => {
  if (!teacherId) {
    console.error('[NOTIFICATION_SERVICE] No teacher ID provided')
    callback([])
    return () => {}
  }

  try {
    const notificationsRef = collection(
      db,
      'teachers',
      teacherId,
      'notifications',
    )

    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(50), // Limit to last 50 notifications
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            type: data.type,
            title: data.title,
            body: data.body,
            link: data.link,
            studentId: data.studentId,
            studentName: data.studentName,
            read: data.read || false,
            createdAt: data.createdAt,
            metadata: data.metadata,
          } as Notification
        })

        console.log(
          `[NOTIFICATION_SERVICE] Received ${notifications.length} notifications`,
        )
        callback(notifications)
      },
      (error) => {
        console.error('[NOTIFICATION_SERVICE] Error in subscription:', error)
        callback([])
      },
    )

    return unsubscribe
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error setting up subscription:',
      error,
    )
    callback([])
    return () => {}
  }
}

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (
  teacherId: string,
  notificationId: string,
): Promise<void> => {
  if (!teacherId || !notificationId) {
    throw new Error('Teacher ID and Notification ID are required')
  }

  try {
    const notificationRef = doc(
      db,
      'teachers',
      teacherId,
      'notifications',
      notificationId,
    )

    await updateDoc(notificationRef, {
      read: true,
    })

    console.log(
      '[NOTIFICATION_SERVICE] Marked notification as read:',
      notificationId,
    )
  } catch (error) {
    console.error('[NOTIFICATION_SERVICE] Error marking as read:', error)
    throw error
  }
}

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (
  teacherId: string,
): Promise<void> => {
  if (!teacherId) {
    throw new Error('Teacher ID is required')
  }

  try {
    const notificationsRef = collection(
      db,
      'teachers',
      teacherId,
      'notifications',
    )

    const q = query(notificationsRef, where('read', '==', false))
    const snapshot = await getDocs(q)

    const updatePromises = snapshot.docs.map((doc) =>
      updateDoc(doc.ref, { read: true }),
    )

    await Promise.all(updatePromises)

    console.log(
      `[NOTIFICATION_SERVICE] Marked ${snapshot.docs.length} notifications as read`,
    )
  } catch (error) {
    console.error('[NOTIFICATION_SERVICE] Error marking all as read:', error)
    throw error
  }
}

/**
 * Helper: Create lesson cancellation notification
 */
export const createLessonCancelledNotification = async (
  teacherId: string,
  lessonData: {
    lessonId: string
    studentId?: string
    studentName?: string
    title?: string
    startAt: string | Date
  },
): Promise<void> => {
  try {
    const lessonDate = new Date(lessonData.startAt)
    const formattedDate = lessonDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })

    await createNotification(teacherId, {
      type: 'lesson',
      title: 'Lesson Cancelled',
      body: `${lessonData.title || 'Lesson'} with ${lessonData.studentName || 'student'} on ${formattedDate} was cancelled`,
      link: '/teacher/calendar2',
      studentId: lessonData.studentId,
      studentName: lessonData.studentName,
      metadata: {
        lessonId: lessonData.lessonId,
      },
    })
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error creating cancellation notification:',
      error,
    )
  }
}

/**
 * Helper: Create lesson rescheduled notification
 */
export const createLessonRescheduledNotification = async (
  teacherId: string,
  lessonData: {
    lessonId: string
    studentId?: string
    studentName?: string
    title?: string
    oldStartAt: string | Date
    newStartAt: string | Date
  },
): Promise<void> => {
  try {
    const oldDate = new Date(lessonData.oldStartAt)
    const newDate = new Date(lessonData.newStartAt)

    const oldFormatted = oldDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })

    const newFormatted = newDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })

    await createNotification(teacherId, {
      type: 'lesson',
      title: 'Lesson Rescheduled',
      body: `${lessonData.title || 'Lesson'} with ${lessonData.studentName || 'student'} moved from ${oldFormatted} to ${newFormatted}`,
      link: '/teacher/calendar2',
      studentId: lessonData.studentId,
      studentName: lessonData.studentName,
      metadata: {
        lessonId: lessonData.lessonId,
      },
    })
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error creating reschedule notification:',
      error,
    )
  }
}

/**
 * Helper: Create task overdue notification
 */
export const createTaskOverdueNotification = async (
  teacherId: string,
  taskData: {
    taskId: string
    studentId: string
    studentName?: string
    title: string
    dueDate: string | Date
  },
): Promise<void> => {
  try {
    const dueDate = new Date(taskData.dueDate)
    const formattedDate = dueDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })

    await createNotification(teacherId, {
      type: 'task',
      title: 'Task Overdue',
      body: `"${taskData.title}" for ${taskData.studentName || 'student'} was due ${formattedDate}`,
      link: '/assigned-tasks',
      studentId: taskData.studentId,
      studentName: taskData.studentName,
      metadata: {
        taskId: taskData.taskId,
      },
    })
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error creating overdue notification:',
      error,
    )
  }
}

/**
 * Helper: Create new message notification
 */
export const createNewMessageNotification = async (
  teacherId: string,
  messageData: {
    conversationId: string
    studentId: string
    studentName?: string
    preview: string
  },
): Promise<void> => {
  try {
    await createNotification(teacherId, {
      type: 'message',
      title: 'New Message',
      body: `${messageData.studentName || 'A student'} sent you a message: "${messageData.preview}"`,
      link: '/teacher-messages',
      studentId: messageData.studentId,
      studentName: messageData.studentName,
      metadata: {
        conversationId: messageData.conversationId,
      },
    })
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error creating message notification:',
      error,
    )
  }
}

/**
 * Check for overdue tasks and create notifications
 * This should be called when loading tasks or on dashboard load
 */
export const checkAndCreateOverdueNotifications = async (
  teacherId: string,
  tasks: Array<{
    id?: string
    title: string
    studentId: string
    studentName?: string
    dueDate: string | null
    status: string
  }>,
): Promise<void> => {
  if (!teacherId || !tasks || tasks.length === 0) {
    return
  }

  try {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    // Get existing overdue notifications to avoid duplicates
    const notificationsRef = collection(
      db,
      'teachers',
      teacherId,
      'notifications',
    )
    const existingQuery = query(notificationsRef, where('type', '==', 'task'))
    const existingSnapshot = await getDocs(existingQuery)
    const existingTaskIds = new Set(
      existingSnapshot.docs
        .map((doc) => doc.data().metadata?.taskId)
        .filter(Boolean),
    )

    // Find overdue tasks that don't have notifications yet
    const overdueTasks = tasks.filter((task) => {
      if (!task.id || !task.dueDate || task.status === 'completed') {
        return false
      }

      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)

      return dueDate < now && !existingTaskIds.has(task.id)
    })

    // Create notifications for newly overdue tasks
    const notificationPromises = overdueTasks.map((task) =>
      createTaskOverdueNotification(teacherId, {
        taskId: task.id!,
        studentId: task.studentId,
        studentName: task.studentName,
        title: task.title,
        dueDate: task.dueDate!,
      }),
    )

    await Promise.all(notificationPromises)

    if (overdueTasks.length > 0) {
      console.log(
        `[NOTIFICATION_SERVICE] Created ${overdueTasks.length} overdue task notifications`,
      )
    }
  } catch (error) {
    console.error('[NOTIFICATION_SERVICE] Error checking overdue tasks:', error)
  }
}

// ============================================================================
// STUDENT NOTIFICATIONS (Phase 2/3)
// ----------------------------------------------------------------------------
// Mirror of the teacher subscription, reading from users/{userId}/notifications.
// Used by the student bell to render message-type notifications in real-time.
// ============================================================================

export const subscribeToStudentNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void,
): (() => void) => {
  if (!userId) {
    console.error('[NOTIFICATION_SERVICE] No student userId provided')
    callback([])
    return () => {}
  }

  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications')

    // Order by updatedAt desc so re-pinged conversations bubble to the top.
    // Fall back to createdAt for older docs that don't carry updatedAt.
    const q = query(notificationsRef, orderBy('updatedAt', 'desc'), limit(50))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            type: data.type,
            title: data.title || '',
            body: data.body || '',
            link: data.link,
            studentId: data.studentId,
            studentName: data.studentName,
            read: data.read || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            metadata: data.metadata,
            conversationId: data.conversationId,
            otherPartyId: data.otherPartyId,
            otherPartyName: data.otherPartyName,
            preview: data.preview,
            count: typeof data.count === 'number' ? data.count : 0,
          } as Notification
        })

        console.log(
          `[NOTIFICATION_SERVICE] Received ${notifications.length} student notifications`,
        )
        callback(notifications)
      },
      (error) => {
        console.error(
          '[NOTIFICATION_SERVICE] Error in student subscription:',
          error,
        )
        callback([])
      },
    )

    return unsubscribe
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error setting up student subscription:',
      error,
    )
    callback([])
    return () => {}
  }
}

export const markStudentNotificationAsRead = async (
  userId: string,
  notificationId: string,
): Promise<void> => {
  if (!userId || !notificationId) {
    throw new Error('User ID and Notification ID are required')
  }

  try {
    const notificationRef = doc(
      db,
      'users',
      userId,
      'notifications',
      notificationId,
    )

    await updateDoc(notificationRef, {
      read: true,
      count: 0,
    })

    console.log(
      '[NOTIFICATION_SERVICE] Marked student notification as read:',
      notificationId,
    )
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error marking student notification as read:',
      error,
    )
    throw error
  }
}

// ============================================================================
// EXTENDED TEACHER SUBSCRIPTION (Phase 2/3)
// ----------------------------------------------------------------------------
// Like subscribeToNotifications but maps the full message-notification
// shape (conversationId, count, preview, otherPartyName, updatedAt).
// Kept as a separate exported function so we don't break callers that
// rely on the original simpler shape.
// ============================================================================

export const subscribeToTeacherStoredNotifications = (
  teacherId: string,
  callback: (notifications: Notification[]) => void,
): (() => void) => {
  if (!teacherId) {
    console.error('[NOTIFICATION_SERVICE] No teacher ID provided')
    callback([])
    return () => {}
  }

  try {
    const notificationsRef = collection(
      db,
      'teachers',
      teacherId,
      'notifications',
    )

    const q = query(notificationsRef, orderBy('updatedAt', 'desc'), limit(50))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            type: data.type,
            title: data.title || '',
            body: data.body || '',
            link: data.link,
            studentId: data.studentId,
            studentName: data.studentName,
            read: data.read || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            metadata: data.metadata,
            conversationId: data.conversationId,
            otherPartyId: data.otherPartyId,
            otherPartyName: data.otherPartyName,
            preview: data.preview,
            count: typeof data.count === 'number' ? data.count : 0,
          } as Notification
        })

        callback(notifications)
      },
      (error) => {
        console.error(
          '[NOTIFICATION_SERVICE] Error in teacher stored subscription:',
          error,
        )
        callback([])
      },
    )

    return unsubscribe
  } catch (error) {
    console.error(
      '[NOTIFICATION_SERVICE] Error setting up teacher stored subscription:',
      error,
    )
    callback([])
    return () => {}
  }
}
